import React, { useState } from "react";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import * as Yup from "yup";
import FilterFind from "../../../components/FilterFind";
import {
  ButtonComp,
  CardComp,
  LoanTypeDetails,
  MoneyInputComp,
  SelectComp2,
  TextInputComp,
} from "../../../components";
import { useEnums } from "../../../hooks/useEnums";
import {
  useGetSelectableLoanTypesQuery,
  useLoanCalculatorMutation,
} from "../../../store/admin/loan";
import FormalLoanCalculationResult from "../../Members/LoanManagement/FormalLoanCalculationResult";
import InformalLoanCalculationResult from "../../Members/LoanManagement/InformalLoanCalculationResult";

/**
 * Staff loan calculator — same schedule math as member; repayment frequency follows duration type.
 */
export default function AdminLoanCalculator() {
  const enums = useEnums();
  const { data: loanTypes, isLoading: isLoadingLoanTypes } =
    useGetSelectableLoanTypesQuery();
  const [calculateLoan, { isLoading }] = useLoanCalculatorMutation();
  const [result, setResult] = useState(null);

  const formattedLoanTypes = (loanTypes?.data ?? [])
    .filter((item) => item && item.id)
    .map((item) => ({
      label: item.name,
      value: item.id,
      id: item.id,
      rate: item.rate,
      interest_calculation_mode: item.interest_calculation_mode,
      min_loan_amount: item.min_loan_amount,
      max_loan_amount: item.max_loan_amount,
      loan_fee: item.loan_fee,
      ...item,
    }));

  const handleSubmit = async (values) => {
    const payload = {
      loan_type_id: values.loan_type_id,
      amount: values.amount,
      duration: values.duration,
      duration_type: values.duration_type,
    };
    try {
      const response = await calculateLoan(payload).unwrap();
      if (response?.status === false) {
        return toast.error(response?.message || "Calculation failed");
      }
      toast.success(response?.message || "Loan calculated successfully");
      setResult(response?.data ?? null);
    } catch (error) {
      toast.error(error?.data?.message || "An error occurred");
    }
  };

  const isInformal =
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    result.loan_model === "informal";
  const rows = Array.isArray(result)
    ? result
    : result?.rows ?? result?.data ?? [];

  return (
    <>
      <FilterFind back={true} rightSide={false} title="Loan Calculator" />
      <CardComp cardClassName="border-0 px-lg-4 pt-4 pb-4 mb-4 shadow3">
        <Formik
          initialValues={{
            loan_type_id: "",
            amount: "",
            duration: "",
            duration_type: "",
          }}
          validationSchema={Yup.object({
            loan_type_id: Yup.string().required("Required"),
            amount: Yup.string().required("Required"),
            duration: Yup.string().required("Required"),
            duration_type: Yup.string().required("Required"),
          })}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => {
            const selectedLoanType =
              formattedLoanTypes.find(
                (item) => String(item.value) === String(values.loan_type_id)
              ) || null;

            return (
            <Form className="row gy-3">
              <div className="col-12 col-lg-6">
                <SelectComp2
                  LabelClassName="fw-2"
                  placeholder="Select loan type"
                  labelText="Loan type"
                  arrayComp={formattedLoanTypes}
                  isLoading={isLoadingLoanTypes}
                  selectedOption={values.loan_type_id}
                  setSelectedOption={(selectedOption) =>
                    setFieldValue("loan_type_id", selectedOption?.value)
                  }
                  errors={
                    touched.loan_type_id && errors.loan_type_id
                      ? [errors.loan_type_id]
                      : []
                  }
                />
              </div>
              {selectedLoanType && (
                <div className="col-12">
                  <LoanTypeDetails
                    loanType={selectedLoanType}
                    variant="calculator"
                    durationType={values.duration_type}
                  />
                </div>
              )}
              <div className="col-12 col-lg-6">
                <MoneyInputComp
                  LabelText="Loan amount"
                  borderColor="#A1A0A0"
                  LabelClassName="fw-2"
                  placeholder="Enter amount"
                  name="amount"
                />
              </div>
              <div className="col-12 col-lg-6">
                <TextInputComp
                  LabelText="Duration"
                  borderColor="#A1A0A0"
                  LabelClassName="fw-2"
                  placeholder="0"
                  name="duration"
                  type="text"
                />
              </div>
              <div className="col-12 col-lg-6">
                <SelectComp2
                  LabelClassName="fw-2"
                  labelText="Duration type"
                  arrayComp={enums["DurationType"] ?? []}
                  selectedOption={values.duration_type}
                  setSelectedOption={(selectedOption) =>
                    setFieldValue("duration_type", selectedOption?.value)
                  }
                  errors={
                    touched.duration_type && errors.duration_type
                      ? [errors.duration_type]
                      : []
                  }
                />
              </div>
              <div className="col-12">
                <p className="text-muted small mb-2">
                  Repayments follow the duration type (e.g. months → monthly).
                </p>
                <ButtonComp
                  loading={isLoading}
                  type="submit"
                  btnText="Calculate loan interest"
                  ButtonInlineStyle={{
                    backgroundColor: "var(--color-primary)",
                  }}
                  btnClassName="border-0 text-white py-2 px-4 rounded"
                />
              </div>
            </Form>
            );
          }}
        </Formik>
      </CardComp>

      {result != null &&
        (isInformal ? (
          <InformalLoanCalculationResult data={result} />
        ) : (
          <FormalLoanCalculationResult data={rows} />
        ))}
    </>
  );
}
